import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { Pill } from "@/components/podkit/status/Pill";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { RunnerActionsDropdown } from "@/components/runners/RunnerActionsDropdown";
import { RunnerCreatedAt } from "@/components/runners/RunnerCreatedAt";
import { RunnerCreatedBy } from "@/components/runners/RunnerCreatedBy";
import { RunnerIcon } from "@/components/runners/RunnerIcon";
import { RunnerName } from "@/components/runners/RunnerName";
import { RunnerPhaseTag } from "@/components/runners/RunnerPhaseTag";
import { RunnerProviderLabel } from "@/components/runners/RunnerProvider";
import { Tooltip } from "@/components/Tooltip";
import { RunnerGroupAccess } from "@/components/runners/details/RunnerGroupAccess";
import { VisibilityControlledByFeatureFlag } from "@/components/VisibilityControlledByFeatureFlag";
import { Flags } from "@/feature-flags";
import { useListGroups, useListRoleAssignments } from "@/queries/group-queries";
import type { RunnerPlaceholder } from "@/queries/runner-manager-queries";
import { type PlainRunner, useCreateManagedRunner } from "@/queries/runner-queries";
import { RunnerKind, RunnerProvider, type SCMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { type FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const RunnerCard: FC<{ runner: PlainRunner; integrations: SCMIntegration[]; isPhasedOut?: boolean }> = ({
    runner,
    integrations,
    isPhasedOut = false,
}) => {
    const navigate = useNavigate();
    const isSelectable = runner.kind === RunnerKind.REMOTE;

    const isNoRepoAccessConfigured = integrations && integrations.length === 0;
    const isConnected = runner.status?.phase === RunnerPhase.ACTIVE || runner.status?.phase === RunnerPhase.DEGRADED;

    const handleOnClick = useCallback(() => {
        if (!isSelectable) {
            return;
        }
        navigate(`/settings/runners/${runner.runnerId}`);
    }, [isSelectable, navigate, runner.runnerId]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!isSelectable) {
                return;
            }
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOnClick();
            }
        },
        [isSelectable, handleOnClick],
    );

    return (
        <div
            data-testid={"runner-" + runner.runnerId}
            role={isSelectable ? "button" : undefined}
            tabIndex={isSelectable ? 0 : undefined}
            onClick={handleOnClick}
            onKeyDown={handleKeyDown}
            className={cn(
                "flex flex-col gap-2 rounded-xl border-0.5 border-solid border-border-base bg-surface-glass px-5 py-4 text-left text-xs",
                isSelectable
                    ? "cursor-pointer hover:outline hover:outline-1 hover:outline-border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-brand"
                    : "cursor-default",
            )}
            aria-label={isSelectable ? `View details for ${runner.name}` : undefined}
            data-tracking-id="view-runner-details-runner-card"
        >
            <div className="flex grow">
                <div className="flex grow flex-col gap-0.5">
                    <div className="flex flex-row">
                        <div className="grow">
                            <RunnerIcon runner={runner} />
                        </div>
                        <div>
                            <RunnerActionsDropdown runner={runner} showViewDetails={true} />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <RunnerName name={runner.name} className="max-w-48" />
                        {isPhasedOut && (
                            <Tooltip content="This runner can still start existing environments, but cannot create new ones">
                                <Pill variant="warning" size="sm">
                                    Phasing Out
                                </Pill>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
            <div className="flex flex-row gap-2">
                <RunnerPhaseTag runner={runner} />
                {isNoRepoAccessConfigured && isConnected && <NoRepoAccessConfigured />}
            </div>
            <div className="flex flex-col gap-1">
                <div className="flex flex-col">
                    <RunnerCreatedAt runner={runner} />
                    <RunnerCreatedBy runner={runner} />
                </div>
                <VisibilityControlledByFeatureFlag featureFlag={Flags.GroupManagementEnabled}>
                    <RunnerGroupAccessWithLabel runnerId={runner.runnerId} />
                </VisibilityControlledByFeatureFlag>
            </div>
        </div>
    );
};
export const AddNewRunnerCard: FC<{ setShowNewRunnerModal: (show: boolean) => void }> = ({ setShowNewRunnerModal }) => {
    return (
        <div data-testid="add-new-runner-card" className="min-h-40 rounded-lg text-content-secondary">
            <Button
                variant="ghost"
                className="flex size-full flex-col gap-0 border-0.5 border-border-light bg-surface-primary text-content-secondary hover:bg-surface-hover"
                onClick={() => setShowNewRunnerModal(true)}
                data-tracking-id="open-runner-card-actions"
            >
                <IconPlus size="base" />
                Set up a new runner
            </Button>
        </div>
    );
};

export const PlaceholderCard: FC<{ placeholder: RunnerPlaceholder }> = ({ placeholder }) => {
    const createManagedRunner = useCreateManagedRunner();
    const { toast } = useToast();

    const handleEnable = useCallback(async () => {
        try {
            await createManagedRunner.mutateAsync({
                runnerManagerId: placeholder.runnerManagerId,
                name: placeholder.name,
                region: placeholder.region,
            });
            toast({
                title: "Runner enabled",
                description: `${placeholder.name} is being set up`,
            });
        } catch (error) {
            toast({
                title: "Failed to enable runner",
                description: error instanceof Error ? error.message : "Please try again",
            });
        }
    }, [placeholder, createManagedRunner, toast]);

    return (
        <div
            data-testid={"placeholder-" + placeholder.runnerManagerId}
            className={cn(
                "flex flex-col gap-2 rounded-xl border-0.5 border-solid border-border-base bg-surface-glass px-5 py-4 text-left text-xs",
                "cursor-default", // Not selectable but normal appearance
            )}
            aria-label="Available Runner Manager"
        >
            <div className="flex grow">
                <div className="flex grow flex-col gap-0.5">
                    <div className="flex flex-row">
                        <div className="grow opacity-60">
                            <RunnerIcon runner={{ provider: RunnerProvider.MANAGED } as PlainRunner} />
                        </div>
                        <div>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleEnable}
                                disabled={createManagedRunner.isPending}
                                loading={createManagedRunner.isPending}
                                data-tracking-id="enable-runner-runner-card"
                            >
                                Enable
                            </Button>
                        </div>
                    </div>
                    <div className="opacity-60">
                        <RunnerName name={placeholder.name} className="max-w-48" />
                    </div>
                    <div className="opacity-60">
                        <RunnerProviderLabel provider={RunnerProvider.MANAGED} />
                    </div>
                </div>
            </div>
            <div className="flex flex-row gap-2">
                <Pill variant="default">Disabled</Pill>
            </div>
            <div className="flex flex-col">
                <div className="text-xs text-content-tertiary">Region: {placeholder.region}</div>
                <div className="text-xs text-content-tertiary">by Ona</div>
            </div>
        </div>
    );
};

const NoRepoAccessConfigured: FC = () => {
    return (
        <Pill
            tooltip={
                <div>
                    <div>Source control is not configured for this runner.</div>
                    <div>
                        Please, configure a repository provider (GitHub, GitLab, Bitbucket or Azure DevOps) to access
                        your repo.
                    </div>
                </div>
            }
            variant="warning"
        >
            No repo access
        </Pill>
    );
};

const RunnerGroupAccessWithLabel: FC<{ runnerId: string }> = ({ runnerId }) => {
    const { data: roleAssignmentsData, isLoading: isLoadingAssignments } = useListRoleAssignments({
        resourceType: ResourceType.RUNNER,
        resourceId: runnerId,
    });

    const { data: groupsData, isLoading: isLoadingGroups } = useListGroups();

    if (isLoadingAssignments || isLoadingGroups) {
        return null;
    }

    const assignments = roleAssignmentsData?.assignments ?? [];
    const groups = groupsData?.groups ?? [];
    const groupIds = new Set(assignments.map((a) => a.groupId));
    const groupsWithAccess = groups.filter((g) => groupIds.has(g.id));

    if (groupsWithAccess.length === 0) {
        return null;
    }

    return (
        <div className="flex items-center gap-2">
            <span className="text-xs text-content-tertiary">Shared with:</span>
            <RunnerGroupAccess runnerId={runnerId} maxVisible={3} />
        </div>
    );
};
